import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { Button } from 'reactstrap'
import { ThreeBounce } from 'better-react-spinkit'
import { useTranslation } from '../../../../../store/context/TranslationContext'
import { useReviewPrimaryActionContext } from '../../../../../components/ReviewWizard/useReviewPrimaryAction'
import { debugReview } from '../../../../../components/ReviewWizard/reviewDebug'
import { buildAutomationChildTestId, resolveAutomationTestId } from '../../../../../components/InputField/testId'

const blurIfButton = (element) => {
    if (typeof window === "undefined") {
        return;
    }

    if (element instanceof HTMLButtonElement && typeof element.blur === "function") {
        element.blur();
        return;
    }

    if (document.activeElement instanceof HTMLButtonElement && typeof document.activeElement.blur === "function") {
        document.activeElement.blur();
    }
};

const ButtonGroupNew = ({
    onNextClick,
    hideBackButton,
    hideSubmitButton,
    onBackClick,
    showBackOnFirstStep = false,
    step,
    asLabel,
    loading,
    limit,
    isDisabled,
    nextText,
    showPageNum,
    backOpen = false,
    pageNum,
    isReadyForReviewAction = true,
    reviewPendingReason = "",
    testId,
    testIdPrefix,
    primaryActionTestIdSuffix,
}) => {

    const { t } = useTranslation()
    const resolvedTestId = resolveAutomationTestId(testId, testIdPrefix);
    const reviewPrimaryAction = useReviewPrimaryActionContext();
    const registerPrimaryAction = reviewPrimaryAction?.registerPrimaryAction;
    const registerBackAction = reviewPrimaryAction?.registerBackAction;
    const loadingRef = useRef(Boolean(loading));
    const isMountedRef = useRef(true);
    const nextClickRef = useRef(onNextClick);
    const backClickRef = useRef(onBackClick);
    const primaryActionLabel = useMemo(() => (
        nextText ? nextText : step === limit ? t("Submit") : t("Next")
    ), [limit, nextText, step, t]);
    const resolvedPrimaryActionTestIdSuffix = primaryActionTestIdSuffix || (step === limit ? "submit" : "next");
    const backActionLabel = useMemo(() => (
        t("Back")
    ), [t]);
    const navigationIdentity = useMemo(() => (
        [
            pageNum ?? "no-page",
            step ?? "no-step",
            hideBackButton ? "hide-back" : "show-back",
            hideSubmitButton ? "hide-next" : "show-next",
        ].join(":")
    ), [hideBackButton, hideSubmitButton, pageNum, step]);
    const actionId = useMemo(() => (
        `review-primary-action:${navigationIdentity}`
    ), [navigationIdentity]);
    const backActionId = useMemo(() => (
        `review-back-action:${navigationIdentity}`
    ), [navigationIdentity]);

    useEffect(() => {
        loadingRef.current = Boolean(loading);
    }, [loading]);

    useEffect(() => {
        nextClickRef.current = onNextClick;
    }, [onNextClick]);

    useEffect(() => {
        backClickRef.current = onBackClick;
    }, [onBackClick]);

    useEffect(() => () => {
        isMountedRef.current = false;
    }, []);

    const waitForLoadingCycle = useCallback(async () => {
        await new Promise((resolve) => {
            if (typeof window === "undefined") {
                resolve();
                return;
            }
            window.setTimeout(resolve, 0);
        });

        if (!loadingRef.current) {
            const graceStartedAt = Date.now();

            while (!loadingRef.current && (Date.now() - graceStartedAt) < 250) {
                await new Promise((resolve) => {
                    if (typeof window === "undefined") {
                        resolve();
                        return;
                    }
                    window.setTimeout(resolve, 25);
                });
            }
        }

        if (!loadingRef.current) {
            return false;
        }

        await new Promise((resolve) => {
            const startedAt = Date.now();

            const poll = () => {
                if (!isMountedRef.current || !loadingRef.current || (Date.now() - startedAt) > 30000) {
                    resolve();
                    return;
                }

                window.setTimeout(poll, 50);
            };

            poll();
        });

        return true;
    }, []);

    const shouldSkipLoadingWait = useCallback((actionResult) => (
        Number.isFinite(actionResult?.status) && actionResult.status >= 400
    ), []);

    const invokeRegisteredAction = useCallback(async (handlerRef) => {
        const handler = typeof handlerRef === "function" ? handlerRef : handlerRef?.current;
        if (typeof handler !== "function") {
            return undefined;
        }

        blurIfButton();

        const syntheticEvent = {
            preventDefault: () => { },
            stopPropagation: () => { },
        };

        debugReview("button-group.invoke.start", {
            actionId,
            backActionId,
            pageNum,
            step,
            loading: loadingRef.current,
            isReadyForReviewAction,
            reviewPendingReason,
        });

        const result = handler(syntheticEvent);
        if (result && typeof result.then === "function") {
            const awaitedResult = await result;
            if (shouldSkipLoadingWait(awaitedResult)) {
                debugReview("button-group.invoke.end", {
                    actionId,
                    pageNum,
                    step,
                    result: awaitedResult,
                    loadingAfter: loadingRef.current,
                });
                return awaitedResult;
            }
            const didWaitForLoading = await waitForLoadingCycle();
            debugReview("button-group.invoke.end", {
                actionId,
                pageNum,
                step,
                result: awaitedResult,
                loadingAfter: loadingRef.current,
                didWaitForLoading,
            });
            return awaitedResult;
        }

        if (shouldSkipLoadingWait(result)) {
            debugReview("button-group.invoke.end", {
                actionId,
                pageNum,
                step,
                result,
                loadingAfter: loadingRef.current,
            });
            return result;
        }

        const didWaitForLoading = await waitForLoadingCycle();
        debugReview("button-group.invoke.end", {
            actionId,
            pageNum,
            step,
            result,
            loadingAfter: loadingRef.current,
            didWaitForLoading,
        });
        return result;
    }, [
        actionId,
        backActionId,
        isReadyForReviewAction,
        pageNum,
        reviewPendingReason,
        shouldSkipLoadingWait,
        step,
        waitForLoadingCycle,
    ]);

    const handleNextButtonClick = useCallback((event) => {
        blurIfButton(event?.currentTarget);
        return nextClickRef.current?.(event);
    }, []);

    const handleBackButtonClick = useCallback((event) => {
        blurIfButton(event?.currentTarget);
        return backClickRef.current?.(event);
    }, []);

    const handleRegisteredNextAction = useCallback(
        () => invokeRegisteredAction(nextClickRef),
        [invokeRegisteredAction]
    );

    const handleRegisteredBackAction = useCallback(
        () => invokeRegisteredAction(backClickRef),
        [invokeRegisteredAction]
    );

    const hasNextHandler = typeof onNextClick === "function";
    const hasBackHandler = typeof onBackClick === "function";

    useEffect(() => {
        if (!registerPrimaryAction) {
            return undefined;
        }

        if (asLabel || hideSubmitButton || !hasNextHandler) {
            debugReview("button-group.register.primary", {
                actionId,
                pageNum,
                step,
                hidden: true,
            });
            return registerPrimaryAction({
                id: actionId,
                hidden: true,
            });
        }

        debugReview("button-group.register.primary", {
            actionId,
            pageNum,
            step,
            disabled: Boolean(loading || isDisabled || !isReadyForReviewAction),
            hidden: false,
            loading: Boolean(loading),
            notReady: !isReadyForReviewAction,
            pendingReason: reviewPendingReason,
        });
        return registerPrimaryAction({
            id: actionId,
            label: primaryActionLabel,
            onAction: handleRegisteredNextAction,
            disabled: Boolean(loading || isDisabled || !isReadyForReviewAction),
            loading: Boolean(loading),
            notReady: !isReadyForReviewAction,
            pendingReason: !isReadyForReviewAction ? reviewPendingReason : "",
        });
    }, [
        asLabel,
        hideSubmitButton,
        isDisabled,
        isReadyForReviewAction,
        loading,
        hasNextHandler,
        primaryActionLabel,
        actionId,
        handleRegisteredNextAction,
        pageNum,
        reviewPendingReason,
        registerPrimaryAction,
        step,
    ]);

    useEffect(() => {
        if (!registerBackAction) {
            return undefined;
        }

        if (asLabel || hideBackButton || !hasBackHandler || (step === 1 && !showBackOnFirstStep)) {
            return registerBackAction({
                id: backActionId,
                hidden: true,
            });
        }

        return registerBackAction({
            id: backActionId,
            label: backActionLabel,
            onAction: handleRegisteredBackAction,
            disabled: Boolean((loading || isDisabled) && !backOpen),
            loading: Boolean(loading),
        });
    }, [
        asLabel,
        backOpen,
        hideBackButton,
        isDisabled,
        loading,
        hasBackHandler,
        backActionId,
        backActionLabel,
        handleRegisteredBackAction,
        registerBackAction,
        showBackOnFirstStep,
        step,
    ]);

    return (
        <>
            {
                !!asLabel ? null : (
                    <div
                        className='d-flex justify-content-between align-items-center'
                        data-testid={resolvedTestId}
                    >
                        {
                            ((step === 1 && !showBackOnFirstStep) || hideBackButton) ? <div style={{ minWidth: "100px" }} data-testid={buildAutomationChildTestId(resolvedTestId, "back-placeholder")}></div> : <Button
                                className='form-content-btn-outline'
                                onClick={handleBackButtonClick}
                                disabled={(loading || isDisabled) && !backOpen}
                                data-testid={buildAutomationChildTestId(resolvedTestId, "back")}
                            >
                                {loading ? <ThreeBounce color="#000" /> : backActionLabel}
                            </Button>
                        }
                        {showPageNum ? <p className='fs-5 mt-2' data-testid={buildAutomationChildTestId(resolvedTestId, "page")}>{pageNum}</p> : <div data-testid={buildAutomationChildTestId(resolvedTestId, "page-placeholder")}></div>}
                        {hideSubmitButton ? <div style={{ minWidth: "100px" }} data-testid={buildAutomationChildTestId(resolvedTestId, "next-placeholder")}></div> :
                            <Button
                                className={`form-content-btn ${(isDisabled || !isReadyForReviewAction) ? 'disabled' : ''}`}
                                onClick={handleNextButtonClick}
                                disabled={loading || isDisabled || !isReadyForReviewAction}
                                data-testid={buildAutomationChildTestId(resolvedTestId, resolvedPrimaryActionTestIdSuffix)}
                            >
                                {loading ? <ThreeBounce color="#FFF" /> : primaryActionLabel}
                            </Button>
                        }
                    </div>
                )
            }
        </>
    )
}

export default ButtonGroupNew;
